import m, {Vnode, VnodeDOM} from "mithril";
import ICustomFormSettings from "../../ICustomFormSettings";
import {TextboxItem} from "../../../model/items/textbox";
import {CheckboxItem} from "../../../model/items/checkbox";
import {LabelItem} from "../../../model/items/labelItem";
import CheckboxCell from "../../shared/components/FormCell/CheckboxCell";
import TextboxCell from "../../shared/components/FormCell/TextboxCell";
import GroupFilter from "../GroupFilter";
import TMapPointSetting, {TMapPoint} from "./TMapPointSetting";
import SeznamMapCustomForm from "../SeznamMapCustomForm";
import L from "leaflet";
import "leaflet.markercluster";
import {loadStyles} from "../../../model/utils";

require('jquery-autocomplete');

export default class MapPoint extends SeznamMapCustomForm {

    private cellItemId!: TextboxItem;
    private cellItemGroup: TextboxItem|undefined;
    private cellItemGpsLat!: TextboxItem;
    private cellItemGpsLng!: TextboxItem;
    private cellItemLabel!: TextboxItem;
    private cellItemJsonData!: TextboxItem;
    private cellItemOther: CheckboxItem|undefined;
    private cellItemGU: CheckboxItem|undefined;
    private mapData: TMapPoint[];
    private mapSetting: TMapPointSetting;

    private map!: L.Map;
    private brandFilter: GroupFilter;
    private suggestEl?: Element;
    public readonly self: MapPoint;

    private selectedPoint?: TMapPoint;

    private cellGU?: CheckboxCell;
    private cellOther?: CheckboxCell;
    private cellLabel?: TextboxCell;

    private initPhase = false;
    private loading = true;

    constructor(settings: ICustomFormSettings) {
        super(settings);

        const layout = settings.form.grid.layout;
        this.cellItemId       = <TextboxItem>layout[0][1].cellItems[0];
        this.cellItemGroup    = <TextboxItem>layout[1][1].cellItems[0];
        this.cellItemGpsLat   = <TextboxItem>layout[2][1].cellItems[0];
        this.cellItemGpsLng   = <TextboxItem>layout[3][1].cellItems[0];
        this.cellItemLabel    = <TextboxItem>layout[4][1].cellItems[0];
        this.cellItemJsonData = <TextboxItem>layout[5][1].cellItems[0];
        this.cellItemOther    = <CheckboxItem>layout[6]?.[1]?.cellItems?.[0];
        this.cellItemGU       = <CheckboxItem>layout[7]?.[1]?.cellItems?.[0];

        // MAP DATA
        this.mapData = [];
        // Load data from label
        const data = (<LabelItem>layout[8][1].cellItems[0])?.childrenTags[0]?.value!;
        try {
            // It can be JSON directly
            this.mapData = JSON.parse(data);
        } catch (e) {
            // Or something else
            this.mapData = [];
        }
        // Try use string from label as url of included file of survey
        if (!this.mapData.length) {
            // @ts-ignore (type harassment)
            m.request({
                url: this.settings.form.questionary.getFileUrl(data),
                responseType: "json",
            }).then((result) => {
                this.mapData = <TMapPoint[]>result;
                this.init();
            });
        }

        // MAP SETTING
        try {
            const mapSetting = (<LabelItem>layout[9][1].cellItems[0])?.childrenTags[0]?.value;
            this.mapSetting = mapSetting ? JSON.parse(mapSetting) : {};
        } catch (e) { // JSON parse error
            console.warn('customForm-Map-point: Map settings is not valid json.')
            this.mapSetting = {};
        }

        if (this.cellItemOther) {
            this.cellOther = new CheckboxCell(this.cellItemOther.cell, (cellItem) => {
                if (this.cellItemOther?.getVariable()) {
                    // uncheck
                    cellItem.set();
                } else {
                    // reset whole form, reset map
                    this.loading = true;
                    m.redraw();
                    this.reset();
                    this.resetMap();
                    cellItem.set();
                }
                m.redraw();
            });
            this.cellLabel = new TextboxCell(this.cellItemLabel);

            // @ts-ignore  polyfill for IE
            if (!String.prototype.normalize){String.prototype.normalize = function(){return this;};}
        }
        if (this.cellItemGU) {
            this.cellGU = new CheckboxCell(this.cellItemGU.cell, (cellItem) => {
                cellItem.set();
                if (this.cellItemGU?.getVariable()) {
                    this.loading = true;
                    this.reset();
                    this.resetMap();
                    m.redraw();
                }
            });
        }
        this.brandFilter = new GroupFilter(this.settings.form.questionary, this.mapSetting)

        this.self = this;
    }

    // Wait on seznam map library and map data load
    init() {
        this.self.loading = true;
        m.redraw();
        if (this.initPhase) {
            return;
        }
        this.self.initPhase = true;
        const condition = () => this.map && this.mapData?.length && this.suggestEl;
        const job = () => {
            this.initMapCenter();
            this.initMapPoints();
            if (this.selectedPoint) {
                this.zoomToPoint(this.selectedPoint, false);
            }
            this.initSuggest(this.suggestEl!);
            this.self.initPhase = false;
            this.self.loading = false;
            m.redraw();
        }
        const interval = setInterval(() => {
            if (condition()) {
                clearInterval(interval);
                setTimeout(() => job(), 100);
            }
        }, 100);
    }

    public checkRequirements(): boolean {
        if (!this.cellItemId) {
            console.warn("customForm-Map-point: Missing inputText 'ID', cell on row 0, column 1.")
            return false;
        }
        if (!this.cellItemGpsLat) {
            console.warn("customForm-Map-point: Missing inputText 'GpsLat', cell on row 2, column 1.")
            return false;
        }
        if (!this.cellItemGpsLng) {
            console.warn("customForm-Map-point: Missing inputText 'GpsLng', cell on row 3, column 1.")
            return false;
        }
        if (!this.cellItemLabel) {
            console.warn("customForm-Map-point: Missing inputText 'Label', cell on row 4, column 1.")
            return false;
        }
        if (!this.cellItemJsonData) {
            console.warn("customForm-Map-point: Missing inputText 'JsonData', cell on row 5, column 1.")
            return false;
        }
        if (this.cellItemGU && !this.cellItemGU.globalUnknown) {
            console.warn(`customForm-Map-point: Checkbox 'GlobalGU' should be globalUnknown, cell on row 7, column 1.`)
            return false;
        }

        return true;
    }

    private initMapCenter(): void {
        // Map center is on first marker
        let [lat, lng] = this.mapData[0]?.gps || [50,14];
        if (this.mapSetting.center?.length) {
            // Map center is defined in mapSetting
            [lat, lng] = this.mapSetting.center;
        }
        this.map.setView([lat, lng]);

        // Map center on device location
        if (this.mapSetting.useDeviceLocation) {
            navigator.geolocation.getCurrentPosition((pos) => {
                if (!this.selectedPoint) {
                    this.map.setView([pos.coords.latitude, pos.coords.longitude]);
                }
            });
        }

        // Map zoom
        if (this.mapSetting.zoom) {
            this.map.setZoom(this.mapSetting.zoom);
        }

        L.tileLayer(SeznamMapCustomForm.getTilesUrl(), {
            minZoom: 0,
            maxZoom: 19,
            attribution: '<a href="https://api.mapy.cz/copyright" target="_blank">&copy; Seznam.cz a.s. a další</a>'
        }).addTo(this.map);
    }

    private initMapPoints(): void {

        const createMarker = (p: TMapPoint) => {
            const messages = this.settings.form.questionary.messages;
            let url = null;
            if (p.group && this.mapSetting.icons && this.mapSetting.icons[p.group]) {
                url = this.settings.form.questionary.getFileUrl(this.mapSetting.icons[p.group]);
            }
            let icon;
            if (url) {
                icon = L.icon({iconUrl: url})
            }
            const marker = L.marker(p.gps, {
                title: p.label,
                icon: icon,
            });

            const $button = $(`<button class="btn raised primary">${messages.get('select')}</button>`);

            $button.on('click', (e) => {
                e.preventDefault();
                this.selectPoint(p);
                m.redraw();
                $button.addClass('primary').addClass('has-primary-background');
                $button.text(messages.get('continue')).on('click', (e) => {
                    e.preventDefault();
                    this.selectPoint(p); // just in case
                    this.settings.form.questionary.next();
                    m.redraw();
                })
            });
            const $content = $(`<div class="text-center"><p>${p.label}</p></div>`)
            $content.append($button);

            const popup = L.popup({
                maxWidth: 200,
                minWidth: 200,
                maxHeight: 200,
                content: $content.get(0),
            });
            marker.bindPopup(popup);

            return marker;
        }


        const markersGroup = this.mapSetting.clustering ? L.markerClusterGroup() : L.layerGroup();
        const selectedPointId = Number(this.cellItemId.getVariable());
        let hasMarkers = false;
        for (const p of this.mapData) {
            if (this.brandFilter.filter(p)) {
                hasMarkers = true;
                markersGroup.addLayer(createMarker(p));
                if (Number(p.id) === selectedPointId) {
                    this.selectPoint(p);
                }
            }
        }
        this.map.addLayer(markersGroup);

        // Map has no points => skip to next question
        if (!hasMarkers) {
            if (this.settings.form === this.settings.form.questionary.currentForm) {
                console.log('customForm-Map: No points, skip.');
                this.settings.form.questionary.skip(); // force next form
            }
            return;
        }
    }

    private initSuggest(el: Element): void {
        const $el = $(el);

        const tags = [];
        for (const p of this.mapData) {
            if (this.brandFilter.filter(p)) {
                tags.push({
                    id: p.id,
                    title: p.label,
                    value: p.label.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase(),
                    point: p
                });
            }
        }

        $el.autocomplete({
            source: [{
                data: tags,
            }],
            // Search for items includes all words of query (q:"zabka Praha" => "Žabka - Hračany, Praha")
            valid: (value: string, query: string) => {
                const parts = query.split(' ');
                for (const part of parts) {
                    const needle = part.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase();
                    if (value.indexOf(needle.toLowerCase()) ===-1) {
                        return false;
                    }
                }
                return true;
            }
        });

        $el.on('selected.xdsoft', (e, item: {point: TMapPoint}) => {
            this.selectPoint(item.point);
            this.zoomToPoint(item.point);
        });

    }

    private selectPoint(p: TMapPoint): void {
        this.selectedPoint = p;

        this.cellItemOther?.reset();
        this.cellItemGU?.reset();

        this.cellItemId?.set(p.id);
        this.cellItemGroup?.set(p.group);
        this.cellItemGpsLat?.set(p.gps[0]);
        this.cellItemGpsLng?.set(p.gps[1]);
        this.cellItemLabel?.set(p.label);
        this.cellItemJsonData?.set(JSON.stringify(p));
    }

    private zoomToPoint(p: TMapPoint, animate = true): void {
        const coords = p.gps;
        this.map.setView(coords, 17, {animate: animate});
    }

    private reset(): void {
        this.selectedPoint = undefined;

        this.cellItemOther?.reset();

        this.cellItemId?.reset();
        this.cellItemGroup?.reset();
        this.cellItemGpsLat?.reset();
        this.cellItemGpsLng?.reset();
        this.cellItemLabel?.reset();
        this.cellItemJsonData?.reset();
    }

    private resetMap(): void {
        this.map.closePopup();
        this.init();
    }

    public view(): Vnode {
        const messages = this.settings.form.questionary.messages;
        return this.viewLayout([
            this.loading ? this.viewLoading() : null,
            m('.suggest', {}, [
               m('input',  {
                   type: 'text',
                   placeholder: this.settings.form.questionary.messages.get('search'),
                   oncreate: vnode => {
                       this.self.suggestEl = vnode.dom;
                       this.init();
                   },
               })
            ]),
            m('.map-container', {
                ontouchmove: (e: TouchEvent) => {
                    e.preventDefault();
                },
            }, [
                m('.map', {
                    oncreate: async (vnode: VnodeDOM) => {
                        await loadStyles('https://unpkg.com/leaflet@1.9.4/dist/leaflet.css', {integrity: "sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=", crossorigin: ""});
                        await loadStyles('https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.Default.css');

                        this.self.map = L.map(<HTMLElement>vnode.dom, {
                            zoomControl: false,
                        });
                        L.control.zoom({
                            position: "bottomleft",
                        }).addTo(this.map);

                        m.redraw();
                    },
                }, this.map ? '' : messages.get('loading')),
            ]),
            this.viewSummary(),
            this.viewOtherInput(),
            this.viewLabelInput(),
            this.viewGUInput(),
        ]);
    }

    public viewSummary(): Vnode|null {
        const label = this.cellItemLabel.getVariable();
        return m('.summary', {}, label ? [
            m('i.icon.icon-map-maker'),
            m('span', {}, label)
        ] : null);
    }

    public viewOtherInput(): Vnode|null {
        return this.cellOther ? m(this.cellOther) : null;
    }

    public viewGUInput(): Vnode|null {
        return this.cellGU ? m(this.cellGU) : null;
    }

    public viewLabelInput(): Vnode|null {
        return (this.cellLabel && this.cellItemOther?.getVariable()) ? m(this.cellLabel) : null;
    }

}